/* eslint-disable no-console */
import { useEffect } from 'react';
import Clarity from '@microsoft/clarity';

interface ClarityOptions {
	projectId: string;
	isProd: boolean;
	userId?: string;
	sessionId?: string;
	pageId?: string;
	friendlyName?: string;
	autoConsent?: boolean;
}

export const useClarity = ({
	projectId,
	isProd,
	userId,
	sessionId,
	pageId,
	friendlyName,
	autoConsent = true,
}: ClarityOptions) => {
	useEffect(() => {
		if (!isProd) {
			return;
		}
		if (!projectId) {
			console.warn('Clarity project ID is required.');
			return;
		}

		if (typeof window !== 'undefined' && !(window as any).clarity) {
			Clarity.init(projectId);
		}

		if (autoConsent) {
			Clarity.consent();
		}

		if (userId) {
			Clarity.identify(userId, sessionId, pageId, friendlyName);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projectId, userId, sessionId, pageId, friendlyName, autoConsent]);

	const trackEvent = (eventName: string) => {
		try {
			Clarity.event(eventName);
		} catch (err) {
			console.error('Clarity event tracking error:', err);
		}
	};

	const setCustomTag = (key: string, value: string | string[]) => {
		try {
			Clarity.setTag(key, value);
		} catch (err) {
			console.error('Clarity set tag error:', err);
		}
	};

	const grantConsent = () => {
		try {
			Clarity.consent();
		} catch (err) {
			console.error('Clarity consent error:', err);
		}
	};

	const identifyUser = (
		customId: string,
		customSessionId?: string,
		customPageId?: string,
		friendlyName?: string
	) => {
		if (!isProd) {
			return;
		}
		try {
			Clarity.identify(customId, customSessionId, customPageId, friendlyName);
		} catch (err) {
			console.error('Clarity identify error:', err);
		}
	};

	return {
		trackEvent,
		setCustomTag,
		grantConsent,
		identifyUser,
	};
};
